import React from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Zap, CalendarDays } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardSidebar from '../agencyDashboardSidebar';
import JobsTab from './jobsTab/JobsTab';
import ScheduleTab from './scheduleTab/ScheduleTab';
import { useProviderCompany } from '../../../../context/ProviderCompanyContext';

const TABS = [
  { key: 'schedule', label: 'Schedule', Icon: CalendarDays },
  { key: 'jobs',     label: 'Jobs',     Icon: Zap         },
];

const DispatchPage = () => {
  const { providerCompanyId } = useParams();
  const { fetchProviderCompanyData } = useProviderCompany();
  const history  = useHistory();
  const location = useLocation();

  const params   = new URLSearchParams(location.search);
  const activeTab = TABS.some(t => t.key === params.get('tab')) ? params.get('tab') : 'schedule';

  React.useEffect(() => {
    if (providerCompanyId) fetchProviderCompanyData(providerCompanyId);
  }, [providerCompanyId]);

  const setTab = (key) => {
    const sp = new URLSearchParams(location.search);
    sp.set('tab', key);
    history.replace({ pathname: location.pathname, search: sp.toString() });
  };

  const [showSidebar, setShowSidebar] = React.useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-background font-inter">
      <DashboardSidebar isOpen={showSidebar} setIsOpen={setShowSidebar} />

      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* Page header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground">Dispatch</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage jobs, assign officers, and view the weekly schedule
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-6 border-b border-border">
            <div className="flex gap-1">
              {TABS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors relative ${
                    activeTab === key
                      ? 'text-brand-pink'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon size={15} />
                  {label}
                  {activeTab === key && (
                    <motion.div
                      layoutId="dispatch-tab-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-brand-pink"
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.15 }}
            >
              {activeTab === 'jobs' && <JobsTab />}
              {activeTab === 'schedule' && <ScheduleTab />}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default DispatchPage;
